import { AlertListItem, AlertType } from '@/_shared/models/alerts/alert.model';
import { FailedSkuTableGroup } from '@/_shared/models/digital-shelf/ds-alert.model';
import { AlertService } from '@/_shared/services';
import container from '@/_shared/services/service-container';
import { formatDigitalShelfNumber } from '@/_shared/utils/compact-number-format.utils';
import { ChevronRight, Grid2X2 } from 'lucide-react';
import moment from 'moment';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

interface NotificationPanelProps {
  open: boolean;
  onClose: () => void;
  onUnreadCountChange?: (count: number) => void;
}

const PAGE_SIZE = 20;

const ALERT_TYPE_LABEL: Record<AlertType, string> = {
  ds_alert: 'Digital Shelf Alert',
};

// Chip labels for a digital-shelf alert's metadata.failedModules entries —
// trimmed version of FailingSkusLog.tsx's TABLE_TITLE_CONTENT map.
const TABLE_GROUP_LABEL: Record<FailedSkuTableGroup, string> = {
  online_availability: 'SKUs currently out of stock',
  ratings_reviews: 'SKUs below rating benchmark',
  content_quality: 'SKUs below content quality benchmark',
};

interface DsAlertMetadata {
  failedModules?: FailedSkuTableGroup[];
  counts?: Partial<Record<FailedSkuTableGroup, number>>;
}

export const NotificationPanel = ({
  open,
  onClose,
  onUnreadCountChange,
}: NotificationPanelProps) => {
  const alertService = container.get<AlertService>('AlertService');
  const router = useRouter();

  const [alerts, setAlerts] = useState<AlertListItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const sentinelRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    onUnreadCountChange?.(unreadCount);
  }, [unreadCount, onUnreadCountChange]);

  const loadPage = useCallback(
    async (pageOffset: number) => {
      const resp = await alertService.getAlerts(pageOffset, PAGE_SIZE);
      const data = resp.data;
      if (!data) return;

      setAlerts((prev) =>
        pageOffset === 0 ? data.result : [...prev, ...data.result]
      );
      setUnreadCount(data.unreadCount);
      setOffset(pageOffset + data.result.length);
      setHasMore(pageOffset + data.result.length < data.totalCount);
    },
    [alertService]
  );

  useEffect(() => {
    // Fetch once on mount — the panel is always mounted, visibility is
    // CSS-driven, so this isn't re-triggered by `open` toggling.
    loadPage(0).finally(() => setInitialLoading(false));
  }, [loadPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !loadingMore) {
        setLoadingMore(true);
        loadPage(offset).finally(() => setLoadingMore(false));
      }
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, offset, loadPage]);

  const handleMarkAllAsRead = async () => {
    setAlerts((prev) => prev.map((alert) => ({ ...alert, isRead: true })));
    setUnreadCount(0);
    await alertService.markAlertsAsRead();
  };

  const markAlertRead = (alertId: string) => {
    setAlerts((prev) =>
      prev.map((alert) =>
        alert.id === alertId ? { ...alert, isRead: true } : alert
      )
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
    void alertService.markAlertsAsRead([alertId]);
  };

  const handleRowClick = (alert: AlertListItem) => {
    if (!alert.isRead) markAlertRead(alert.id);
    if (alert.metadata?.['redirectUrl']) {
      router.push(alert.metadata?.['redirectUrl']);
      onClose();
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-end justify-center transition-[visibility] duration-300 md:items-stretch md:justify-end ${
        open ? 'visible' : 'invisible delay-300'
      }`}
    >
      <button
        aria-label="Close notifications"
        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
          open ? 'opacity-50' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`relative flex h-[calc(100vh-48px)] w-full flex-col rounded-t-xl bg-white shadow-lg transition-transform duration-300 transform md:h-full md:w-full md:max-w-120 md:translate-y-0 md:rounded-none ${
          open
            ? 'translate-y-0 md:translate-x-0'
            : 'translate-y-full md:translate-x-full'
        }`}
      >
        <div className="h-15 flex items-center justify-between border-b border-gray-border py-5 px-4">
          <div className="flex flex-row items-center">
            <button
              aria-label="Close notifications"
              onClick={onClose}
              className="size-10 flex items-center justify-center"
            >
              <ChevronRight className="size-5" />
            </button>
            <div className="font-semibold text-title">Notifications</div>
          </div>
          {unreadCount > 0 && (
            <button
              className="text-sm text-main hover:text-main-hover disabled:text-global-text-secondary"
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0}
            >
              Mark all as read
            </button>
          )}
        </div>
        <ul className="flex-1 overflow-y-auto">
          {!initialLoading && alerts.length === 0 && (
            <li className="p-5 text-center text-xs text-global-text-secondary">
              No Notifications
            </li>
          )}
          {alerts.map((alert) => {
            return (
              <li
                className={`border-b border-gray-border last:border-b-0 ${alert.isRead ? 'bg-gray-50 text-gray-500' : ''}`}
                key={alert.id}
              >
                <button
                  className="w-full flex gap-4 p-4 items-start text-start"
                  onClick={() => handleRowClick(alert)}
                >
                  <div
                    className={`shrink-0 size-7 flex items-center justify-center rounded-full bg-blue-100 text-blue-800 ring-5 ring-blue-50`}
                  >
                    <Grid2X2 className="stroke-1" />
                  </div>
                  <div className="flex flex-col gap-4 w-full">
                    <div className="flex items-center justify-between">
                      <div className="text-lg font-semibold">
                        {ALERT_TYPE_LABEL[alert.alertType]}
                      </div>
                      <div className="flex flex-row gap-2">
                        <div className="text-xs text-global-text-secondary">
                          {moment(alert.alertDate).format('DD MMM YYYY')}
                        </div>
                        {!alert.isRead && (
                          <div className="mt-0.5 size-2 shrink-0 rounded-full bg-indigo-600" />
                        )}
                      </div>
                    </div>
                    {alert.metadata && (
                      <AlertMetadata
                        metadata={alert.metadata}
                        alertType={alert.alertType}
                      />
                    )}
                  </div>
                </button>
              </li>
            );
          })}
          <li ref={sentinelRef} className="h-1" />
        </ul>
      </div>
    </div>
  );
};

const AlertMetadata = ({
  alertType,
  metadata,
}: {
  alertType: AlertType;
  metadata: Record<string, unknown>;
}) => {
  switch (alertType) {
    case AlertType._ds_alert: {
      const data = metadata as DsAlertMetadata | null;
      const failedModules = data?.failedModules ?? [];

      return (
        failedModules.length > 0 && (
          <div className="text-base font-normal flex flex-col gap-1 pl-2.5 border-l-2 border-gray-400">
            {failedModules.map((tableGroup) => (
              <span key={tableGroup} role="button" tabIndex={0}>
                {`${formatDigitalShelfNumber(data?.counts?.[tableGroup] ?? 0)} ${TABLE_GROUP_LABEL[tableGroup]}`}
              </span>
            ))}
          </div>
        )
      );
    }
    default:
      return null;
  }
};
